<template>
  <component
    :is="tag"
    class="glass-card"
    :class="[
      `glass-card--pad-${padding}`,
      `glass-card--r-${radius}`,
      { 'glass-card--interactive': interactive, 'glass-card--highlight': highlight, 'glass-card--flat': flat },
    ]"
    :role="interactive && tag !== 'button' ? 'button' : undefined"
    :tabindex="interactive && tag !== 'button' ? 0 : undefined"
    @keydown.enter="onKey"
    @keydown.space.prevent="onKey"
  >
    <slot />
  </component>
</template>

<script setup>
/**
 * GlassCard — стеклянная карточка (тёмная поверхность, 1px рамка, мягкий внутренний блик).
 *
 * <GlassCard>…</GlassCard>
 * <GlassCard interactive @click="go">…</GlassCard>      // нажимаемая (hover/press, Enter/Space)
 * <GlassCard highlight padding="sm">…</GlassCard>        // лаймовая рамка + свечение (строка «я» в лидерборде)
 *
 * props: tag ('div'), padding ('none'|'sm'|'md'|'lg'), radius ('md'|'lg'|'xl'),
 *        interactive, highlight, flat (без тени).
 */
import { getCurrentInstance } from "vue";

const props = defineProps({
  tag: { type: String, default: "div" },
  padding: { type: String, default: "md", validator: (v) => ["none", "sm", "md", "lg"].includes(v) },
  radius: { type: String, default: "lg", validator: (v) => ["md", "lg", "xl"].includes(v) },
  interactive: { type: Boolean, default: false },
  highlight: { type: Boolean, default: false },
  flat: { type: Boolean, default: false },
});

const instance = getCurrentInstance();

/** Клавиатурная активация для interactive-карточек (div с role=button). */
function onKey(event) {
  if (!props.interactive || props.tag === "button") return;
  const el = instance && instance.proxy && instance.proxy.$el;
  if (el && event.target === el) el.click();
}
</script>

<style scoped>
.glass-card {
  position: relative;
  display: block;
  width: 100%;
  min-width: 0;
  color: var(--cb-text);
  text-align: left;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.025) 0%, rgba(255, 255, 255, 0) 40%),
    linear-gradient(180deg, var(--cb-surface-2) 0%, var(--cb-surface) 100%);
  border: 1px solid var(--cb-border);
  box-shadow: var(--cb-inner-highlight), var(--cb-shadow-card);
  transition:
    transform var(--cb-dur-fast) var(--cb-ease),
    border-color var(--cb-dur) var(--cb-ease),
    box-shadow var(--cb-dur) var(--cb-ease),
    background-color var(--cb-dur) var(--cb-ease);
}

.glass-card--flat {
  box-shadow: var(--cb-inner-highlight);
}

.glass-card--pad-none {
  padding: 0;
}

.glass-card--pad-sm {
  padding: 10px 12px;
}

.glass-card--pad-md {
  padding: 16px;
}

.glass-card--pad-lg {
  padding: 20px;
}

.glass-card--r-md {
  border-radius: var(--cb-radius-md);
}

.glass-card--r-lg {
  border-radius: var(--cb-radius-lg);
}

.glass-card--r-xl {
  border-radius: var(--cb-radius-xl);
}

.glass-card--interactive {
  cursor: pointer;
  -webkit-user-select: none;
  user-select: none;
}

.glass-card--interactive:hover {
  border-color: var(--cb-border-strong);
}

.glass-card--interactive:active {
  transform: scale(0.985);
}

.glass-card--highlight {
  border-color: var(--cb-lime);
  background:
    linear-gradient(180deg, rgba(221, 245, 90, 0.08) 0%, rgba(221, 245, 90, 0.02) 100%),
    var(--cb-surface);
  box-shadow: var(--cb-glow-sm), inset 0 0 18px rgba(221, 245, 90, 0.06);
}
</style>
